/**
 * Testi italiani del task manager di un set (ADR 0029): scheda «Task», filtri, elenco,
 * kanban, proposte, archiviati, dettaglio con sottotask, relazioni, cronologia e commenti.
 *
 * Le proposte sono il punto delicato: un task proposto (dalla voce, da un agente o da
 * una persona) non parte finché qualcuno non lo approva, e i testi lo dicono chiaro.
 */

/** Testi del task manager. */
export const tasks = {
  /** Scelta del progetto. */
  project: {
    /** Etichetta. */
    label: 'Progetto',
  },
  /** Set senza progetti. */
  noProjects: 'Il set non ha ancora progetti. Aggiungine uno dalla scheda Progetti.',
  /** Scelta della vista. */
  view: {
    /** Nome accessibile della scelta. */
    label: 'Vista dei task',
    /** Elenco raggruppato per stato. */
    list: 'Elenco',
    /** Colonne per gruppo. */
    board: 'Kanban',
  },
  /** Pulsante di creazione. */
  newTask: 'Nuovo task',
  /** Lettura non riuscita. */
  loadFailed: 'Lettura dei task non riuscita. Riprova.',
  /** Nessun task. */
  empty: 'Nessun task in questo progetto.',
  /** Chi crea i task, nello stato vuoto. */
  emptyWho:
    'I task li crei tu con «Nuovo task». L’orchestratore li crea quando divide una richiesta della conversazione. La voce li propone e tu li approvi.',
  /** Esempio di richiesta, nello stato vuoto. */
  emptyExample: 'Per esempio scrivi nella conversazione: «Aggiungi una pagina dei contatti con modulo e mappa».',
  /** Nessun task con i filtri scelti. */
  noMatch: 'Nessun task con questi filtri.',
  /** Riepilogo per gruppo sopra la barra (ADR 0030, tappa 5). */
  stats: {
    /** Titolo della sezione, per gli screen reader. */
    title: 'Riepilogo del progetto',
    /** Lavoro rimasto. */
    open: 'Aperti',
    /** In corso. */
    inProgress: 'In corso',
    /** Bloccati. */
    blocked: 'Bloccati',
    /** Chiusi negli ultimi 7 giorni. */
    doneWeek: 'Fatti negli ultimi 7 giorni',
    /** Più di quanti se ne sono contati. */
    atLeast: 'almeno {{count}}',
    /** Come si conta. */
    note: 'Conteggi del progetto scelto, sottotask compresi.',
  },
  /** Gruppi di stato, come titoli di sezione e di colonna. */
  groups: {
    /** Proposte. */
    proposed: 'Proposti',
    /** Da valutare. */
    backlog: 'Backlog',
    /** Da fare. */
    todo: 'Da fare',
    /** In lavorazione. */
    in_progress: 'In corso',
    /** Fermi. */
    blocked: 'Bloccati',
    /** Conclusi. */
    done: 'Fatti',
    /** Annullati o rifiutati. */
    cancelled: 'Annullati',
  },
  /** Nomi degli stati creati dalla piattaforma, uno per gruppo. */
  stateNames: {
    /** Proposta. */
    proposed: 'Proposto',
    /** Da valutare. */
    backlog: 'Backlog',
    /** Da fare. */
    todo: 'Da fare',
    /** In lavorazione. */
    in_progress: 'In corso',
    /** Fermo. */
    blocked: 'Bloccato',
    /** Concluso. */
    done: 'Fatto',
    /** Annullato. */
    cancelled: 'Annullato',
  },
  /** Priorità. */
  priorities: {
    /** Urgente. */
    urgent: 'Urgente',
    /** Alta. */
    high: 'Alta',
    /** Media. */
    medium: 'Media',
    /** Bassa. */
    low: 'Bassa',
    /** Nessuna. */
    none: 'Nessuna',
  },
  /** Tipi di lavoro. */
  kinds: {
    /** Lavoro da fare. */
    task: 'Task',
    /** Difetto. */
    bug: 'Bug',
    /** Documentazione. */
    docs: 'Documentazione',
    /** Funzionalità vista da chi la usa. */
    story: 'Story',
    /** Obiettivo grande. */
    epic: 'Epic',
    /** Il resto. */
    other: 'Altro',
  },
  /** Chi ha creato il task. */
  origins: {
    /** Una persona. */
    user: 'Persona',
    /** L'orchestratore. */
    orchestrator: 'Orchestratore',
    /** Un subagente. */
    subagent: 'Subagente',
    /** L'agente vocale. */
    voice: 'Voce',
  },
  /** Colonne dell'elenco. */
  columns: {
    /** Chiave leggibile. */
    key: 'Chiave',
    /** Tipo di lavoro. */
    kind: 'Tipo',
    /** Titolo. */
    title: 'Titolo',
    /** Priorità. */
    priority: 'Priorità',
    /** Assegnatario. */
    assignee: 'Assegnato a',
    /** Sottotask chiusi e totali. */
    subtasks: 'Sottotask',
    /** Azioni della riga. */
    actions: 'Azioni',
  },
  /** Filtri e ordine. */
  filters: {
    /** Ricerca. */
    search: 'Cerca nei task',
    /** Tipo. */
    kind: 'Tipo',
    /** Priorità. */
    priority: 'Priorità',
    /** Stato. */
    group: 'Stato',
    /** Assegnatario. */
    assignee: 'Assegnato a',
    /** Nessun assegnatario. */
    nobody: 'Nessuno',
    /** Task in attesa di altri. */
    blocked: 'Attese',
    /** Scelte del filtro sulle attese. */
    blockedOptions: {
      /** Solo i task che aspettano. */
      only: 'Solo chi aspetta',
      /** Solo i task liberi. */
      none: 'Solo chi non aspetta',
    },
    /** Ordine. */
    sort: 'Ordine',
    /** Ordini. */
    sorts: {
      /** Posizione nel kanban. */
      position: 'Posizione',
      /** Priorità. */
      priority: 'Priorità',
      /** Più recenti. */
      created: 'Più recenti',
      /** Ultime modifiche. */
      updated: 'Ultime modifiche',
    },
    /** Interruttore degli archiviati. */
    archived: 'Mostra archiviati',
    /** Toglie tutti i filtri. */
    clear: 'Togli i filtri',
  },
  /** Pagine di un elenco. */
  pages: {
    /** Quanti task sono mostrati. */
    shown: '{{shown}} di {{total}} task',
    /** Carica la pagina seguente. */
    more: 'Carica altri',
    /** Pagina non caricata. */
    failed: 'Caricamento non riuscito. Riprova.',
  },
  /** Etichetta di un task che aspetta altri task aperti. */
  waiting: 'Aspetta {{keys}}',
  /** Come sopra, quando le chiavi non si conoscono. */
  waitingOther: 'Aspetta altri task',
  /** Etichetta di un task archiviato. */
  archivedTag: 'Archiviato',
  /** Sottotask chiusi su totali. */
  subtasksCount: '{{done}} di {{total}}',
  /** Sottotask su una scheda del kanban. */
  subtasksBoard: 'Sottotask {{done}} di {{total}}',
  /** Nessun assegnatario. */
  unassigned: 'Nessuno',
  /** Task archiviati. */
  archived: {
    /** Titolo della sezione. */
    title: 'Task archiviati',
    /** Nessun archiviato. */
    empty: 'Nessun task archiviato.',
    /** Ripristina. */
    restore: 'Ripristina',
    /** Nome accessibile di «Ripristina» su una riga. */
    restoreName: 'Ripristina {{key}}',
    /** Esito. */
    restored: '{{key}} ripristinato.',
    /** Ripristino non riuscito. */
    failed: 'Ripristino non riuscito. Riprova.',
  },
  /** Coda delle proposte. */
  proposals: {
    /** Titolo della sezione. */
    title: 'Proposte da approvare',
    /** Che cosa sono. */
    intro: 'Arrivano dalla voce, dagli agenti o da te. Gli agenti ci lavorano solo dopo l’approvazione.',
    /** Nessuna proposta. */
    none: 'Nessuna proposta da approvare.',
    /** Approva. */
    approve: 'Approva',
    /** Rifiuta. */
    reject: 'Rifiuta',
    /** Nome accessibile di «Approva» su una riga. */
    approveName: 'Approva {{key}}',
    /** Nome accessibile di «Rifiuta» su una riga. */
    rejectName: 'Rifiuta {{key}}',
    /** Esito dell'approvazione. */
    approved: '{{key}} approvato: è fra i task da fare.',
    /** Esito del rifiuto. */
    rejected: '{{key}} rifiutato.',
    /** Domanda della conferma del rifiuto. */
    rejectQuestion: 'Rifiutare la proposta {{key}}?',
    /** Conseguenza del rifiuto. */
    rejectConsequence: 'Il task va fra gli annullati e nessun agente ci lavora.',
    /** Campo del motivo. */
    reasonLabel: 'Motivo',
    /** Aiuto del motivo. */
    reasonExtra: 'Facoltativo. Resta come commento del task.',
    /** Origine della proposta. */
    from: 'Da {{origin}}',
    /** Operazione non riuscita. */
    failed: 'Operazione non riuscita. Riprova.',
    /** Carica altre proposte. */
    more: 'Carica altre',
  },
  /** Dettaglio affiancato o in un cassetto (ADR 0030, tappa 5). */
  panel: {
    /** Nome accessibile del pannello, senza task. */
    label: 'Dettaglio del task',
    /** Chiude il pannello. */
    close: 'Chiudi',
    /** Task dell'indirizzo non trovato. */
    notFound: 'Task non trovato in questo set. Forse è stato eliminato.',
  },
  /** Avanzamento del task per gruppi di stato. */
  stepper: {
    /** Nome accessibile dell'elenco dei passi. */
    label: 'Avanzamento',
  },
  /** Vista kanban. */
  board: {
    /** Nome accessibile della scelta della colonna di una scheda. */
    moveLabel: 'Sposta {{key}}',
    /** Esito dello spostamento. */
    moved: '{{key}} spostato in {{state}}.',
    /** Colonna vuota. */
    emptyColumn: 'Nessun task',
    /** Spiegazione del trascinamento. */
    hint: 'Trascina una scheda in un’altra colonna, o scegli la colonna dal menu della scheda.',
    /** Spostamento non riuscito. */
    failed: 'Spostamento non riuscito. Riprova.',
  },
  /** Titolo di un task, in creazione e in modifica. */
  titleField: {
    /** Avviso vicino al limite di 200 caratteri. */
    left_one: 'Resta {{count}} carattere.',
    /** Avviso vicino al limite, plurale. */
    left_other: 'Restano {{count}} caratteri.',
  },
  /** Creazione di un task. */
  create: {
    /** Titolo del pannello. */
    title: 'Nuovo task',
    /** Pulsante di invio. */
    submit: 'Crea task',
    /** Campo del titolo. */
    titleLabel: 'Titolo',
    /** Campo della descrizione. */
    descriptionLabel: 'Descrizione',
    /** Aiuto della descrizione. */
    descriptionExtra: 'Puoi usare il Markdown.',
    /** Campo del tipo. */
    kindLabel: 'Tipo',
    /** Aiuto del tipo. */
    kindExtra: 'Una story o un epic si divide in task figli.',
    /** Campo della priorità. */
    priorityLabel: 'Priorità',
    /** Campo dello stato iniziale. */
    groupLabel: 'Stato iniziale',
    /** Campo del ruolo dell'agente. */
    assigneeLabel: 'Ruolo dell’agente',
    /** Aiuto del ruolo. */
    assigneeExtra: 'Per esempio frontend o backend. Lascia vuoto se non lo assegni.',
    /** Campo della scadenza. */
    dueLabel: 'Scadenza',
    /** Esito. */
    done: 'Task creato.',
    /** Creazione non riuscita. */
    failed: 'Creazione non riuscita. Riprova.',
  },
  /** Dettaglio di un task. */
  detail: {
    /** Lettura non riuscita. */
    loadFailed: 'Lettura del task non riuscita. Riprova.',
    /** Campo dello stato. */
    state: 'Stato',
    /** Campo della priorità. */
    priority: 'Priorità',
    /** Campo del tipo. */
    kind: 'Tipo',
    /** Assegnatario. */
    assignee: 'Assegnato a',
    /** Assegnatario agente. */
    assigneeAgent: 'Agente {{role}}',
    /** Assegnare a sé stessi. */
    assignMe: 'A me',
    /** Ruolo scritto a mano. */
    customRole: 'Agente {{role}} (ruolo nuovo)',
    /** Scadenza. */
    due: 'Scadenza',
    /** Origine. */
    origin: 'Creato da',
    /** Inizio. */
    started: 'Iniziato',
    /** Fine. */
    completed: 'Concluso',
    /** Titolo della descrizione. */
    description: 'Descrizione',
    /** Titolo della sezione in modifica. */
    editHeading: 'Titolo e descrizione',
    /** Nessuna descrizione. */
    noDescription: 'Nessuna descrizione.',
    /** Modifica di titolo e descrizione. */
    edit: 'Modifica',
    /** Campo del titolo in modifica. */
    titleLabel: 'Titolo',
    /** Campo della descrizione in modifica. */
    descriptionLabel: 'Descrizione',
    /** Salvataggio. */
    save: 'Salva',
    /** Esito del salvataggio. */
    saved: 'Task aggiornato.',
    /** Padre. */
    parent: 'Task padre',
    /** Titolo dei sottotask. */
    subtasks: 'Sottotask',
    /** Nessun sottotask. */
    noSubtasks: 'Nessun sottotask.',
    /** Campo del sottotask nuovo. */
    subtaskLabel: 'Nuovo sottotask',
    /** Pulsante del sottotask nuovo. */
    addSubtask: 'Aggiungi',
    /** Titolo delle relazioni. */
    relations: 'Dipendenze',
    /** Nessuna relazione. */
    noRelations: 'Nessuna dipendenza.',
    /** Tipi di relazione, dal lato del task aperto. */
    relationKinds: {
      /** Aspetta l'altro. */
      blocked_by: 'Aspetta',
      /** L'altro aspetta questo. */
      blocks: 'Blocca',
      /** Collegamento semplice. */
      relates_to: 'Collegato a',
      /** Ripete l'altro. */
      duplicate_of: 'Duplicato di',
      /** L'altro ripete questo. */
      duplicated_by: 'Duplicato da',
    },
    /** Campo del tipo di relazione nuova. */
    relationKindLabel: 'Tipo di relazione',
    /** Campo del task della relazione nuova. */
    relationTaskLabel: 'Altro task',
    /** Aiuto del campo del task. */
    relationTaskExtra: 'Cerca per titolo o chiave in tutti i progetti del set.',
    /** Pulsante della relazione nuova. */
    addRelation: 'Collega',
    /** Pulsante per togliere una relazione. */
    removeRelation: 'Togli',
    /** Nome accessibile di «Togli». */
    removeRelationName: 'Togli la relazione con {{key}}',
    /** Titolo della cronologia. */
    activity: 'Cronologia',
    /** Nessuna voce. */
    noActivity: 'Nessuna modifica registrata.',
    /** Titolo dei commenti. */
    comments: 'Commenti',
    /** Nessun commento. */
    noComments: 'Nessun commento.',
    /** Campo del commento. */
    commentLabel: 'Nuovo commento',
    /** Pulsante del commento. */
    addComment: 'Commenta',
    /** Esito del commento. */
    commented: 'Commento aggiunto.',
    /** Archivia. */
    archive: 'Archivia',
    /** Ripristina. */
    restore: 'Ripristina',
    /** Esito dell'archiviazione. */
    archived: 'Task archiviato.',
    /** Esito del ripristino. */
    restored: 'Task ripristinato.',
    /** Elimina. */
    delete: 'Elimina',
    /** Domanda della conferma. */
    deleteQuestion: 'Eliminare {{key}}?',
    /** Conseguenza. */
    deleteConsequence: 'Spariscono il task, i suoi sottotask, i commenti e la cronologia.',
    /** Esito dell'eliminazione. */
    deleted: '{{key}} eliminato.',
    /** Operazione non riuscita. */
    failed: 'Operazione non riuscita. Riprova.',
  },
  /** Voci della cronologia: chi ha fatto che cosa. */
  activityVerbs: {
    /** Creazione. */
    created: 'ha creato il task',
    /** Campo cambiato. */
    updated: 'ha cambiato {{field}}',
    /** Spostamento. */
    moved: 'ha spostato il task in {{value}}',
    /** Approvazione. */
    approved: 'ha approvato la proposta',
    /** Rifiuto. */
    rejected: 'ha rifiutato la proposta',
    /** Commento. */
    commented: 'ha commentato',
    /** Relazione aggiunta. */
    relation_added: 'ha collegato {{value}}',
    /** Relazione tolta. */
    relation_removed: 'ha tolto una dipendenza',
    /** Archiviazione. */
    archived: 'ha archiviato il task',
    /** Ripristino. */
    restored: 'ha ripristinato il task',
  },
  /** Frasi della cronologia con il valore prima e dopo. */
  history: {
    /** Campo cambiato. */
    changed: 'ha cambiato {{field}}: prima {{before}}, ora {{after}}',
    /** Spostamento fra due stati. */
    movedFrom: 'ha spostato il task: prima {{before}}, ora {{after}}',
    /** Relazione aggiunta. */
    related: 'ha collegato {{key}} ({{kind}})',
    /** Relazione tolta. */
    unrelated: 'ha tolto il collegamento con {{key}}',
    /** Valore vuoto. */
    none: 'nessuno',
  },
  /** Campi nominati dalla cronologia. */
  fields: {
    /** Titolo. */
    title: 'il titolo',
    /** Descrizione. */
    description: 'la descrizione',
    /** Priorità. */
    priority: 'la priorità',
    /** Tipo. */
    kind: 'il tipo',
    /** Assegnatario. */
    assignee: 'l’assegnatario',
    /** Padre. */
    parent: 'il task padre',
    /** Scadenza. */
    due_at: 'la scadenza',
    /** Stato. */
    state: 'lo stato',
  },
  /** Chi agisce, nella cronologia e nei commenti. */
  actors: {
    /** L'agente vocale. */
    voice: 'Voce',
    /** Un agente, per ruolo. */
    agent: 'Agente {{name}}',
    /** Una persona di cui il set non conosce il nome. */
    person: 'Una persona',
  },
  /** Rifiuti del servizio, per codice. */
  errors: {
    /** Ciclo di dipendenze. */
    cycle: 'La dipendenza chiuderebbe un giro: un task finirebbe per aspettare sé stesso.',
    /** Relazione già presente. */
    exists: 'Questa relazione c’è già.',
    /** Troppi livelli. */
    depth: 'Al massimo tre livelli di sottotask.',
    /** Non più una proposta. */
    notProposed: 'Il task non è più una proposta. Aggiorna la pagina.',
    /** Troppi task. */
    limit: 'Il progetto ha troppi task. Elimina quelli che non servono.',
    /** Relazione non valida. */
    relationInvalid: 'Scegli un altro task dello stesso set.',
  },
};

/** Forma dei testi del task manager. */
export type Tasks = typeof tasks;
